import { useMutation, useQuery, useQueryClient } from "react-query";
import { APIGenerator } from "./GenerateAPI";
import { useEffect, useState } from "react";
import { convertFilterObjectToRawFilter, convertFilterToWhereQuery, convertWhereQueryToFilterObject, FilterObject, OperatorQuery } from "./queryURL.utils";
import { AxiosError } from "axios";
import { NavigateOptions, useLocation, useNavigate } from "react-router-dom";



//TYPES
export interface IUseGenericAPI<T, X, V> {
  baseURL?: string;
  APIname: string;
  pageSize?: number;
  orderBy?: keyof T  | `-${Extract<keyof T, string>}` | "" ;
  APIObject?: APIGenerator<T, X, V>;
  preFilter?: Filter<V>;
  directFilter ?: Filter<V>;
  disableGet?: boolean;
  onSucessRefetchRessources?: string[];
  queryKey?: string[];
}

export interface IUseSingleGenericAPI<T, X> {
  baseURL?: string;
  APIname: string;
  id?: string;
  APIObject?: APIGenerator<T, X, any>;
  disableGet?: boolean;
  onSucessRefetchRessources?: string[];
  queryKey?: string[];
}

export interface QueryValue<T> {
  page: string;
  pageSize: string;
  filter: string;
  orderBy: keyof T  | `-${Extract<keyof T, string>}` | "" | undefined;
  search: string;
}

export type OrderByType<T> = keyof T | `-${Extract<keyof T, string>}` | undefined | "";
export type CallbackFunction<T, V> = (result: {
  result?: T;
  error?: V;
}) => void;

  export type Filter<T> = {
    [key in keyof T]?: OperatorQuery;
  }
// T : type of data
// X : type of data for create put
// V : type of Filter
export function useGenericAPI<T, X, V>({
  baseURL,
  APIname,
  pageSize: pageSizeInit,
  orderBy: orderByInit,
  APIObject,
  preFilter,
  disableGet,
  directFilter,
  onSucessRefetchRessources,
  queryKey = []
}: IUseGenericAPI<T, X, V>) {
  if (!APIObject && !baseURL) {
    throw new Error("baseURL or APIObject is required");
  }
  const API = APIObject || new APIGenerator<T, X, V>(baseURL || "");
  
  const {
    getQueryValue,
    setQueryValue: setQueryValueURL,
    removeQueryValue: removeQueryValueURL,
  } = useQueryURL();
  const location = useLocation();
  const queryKeyName = {
    page: APIname + "-page",
    pageSize: APIname + "-pageSize",
    filter: APIname + "-filter",
    orderBy: APIname + "-orderBy",
    search: APIname + "-search",
  };

  const queryClient = useQueryClient();

  const [currentFilter, setCurrentFilter] = useState<string| undefined | null>();
  const [currentFilterObject, setCurrentFilterOject] = useState<FilterObject<V> |undefined | null>();
  const prefilterString = convertFilterToWhereQuery(preFilter);
  
  // Initialiser queryValue directement depuis l'URL au premier render
  const getInitialQueryValue = (): QueryValue<T> => {
    const page = getQueryValue(queryKeyName.page);
    const pageSize = getQueryValue(queryKeyName.pageSize);
    const filter = getQueryValue(queryKeyName.filter);
    const orderBy = getQueryValue(queryKeyName.orderBy) as keyof T | `-${Extract<keyof T, string>}` | undefined;
    const search = getQueryValue(queryKeyName.search);
    
    return {
      page: page || "1",
      pageSize: pageSize || (pageSizeInit ? pageSizeInit.toString() : "0"),
      filter: (filter || ""),
      orderBy: orderBy || (orderByInit ? orderByInit : undefined),
      search: search || "",
    };
  };
  
  const [queryValue, setQueryValue] = useState<QueryValue<T>>(() => getInitialQueryValue());
  
  useEffect(() => {
    const page = getQueryValue(queryKeyName.page);
    const pageSize = getQueryValue(queryKeyName.pageSize);
    const filter = getQueryValue(queryKeyName.filter);
    const orderBy = getQueryValue(queryKeyName.orderBy) as keyof T | `-${Extract<keyof T, string>}` | undefined;
    const search = getQueryValue(queryKeyName.search);

    setCurrentFilter(filter);
    const objFilter = convertWhereQueryToFilterObject(filter );
    setCurrentFilterOject(objFilter as any);

    setQueryValue({
      page: page || "1",
      pageSize: pageSize || (pageSizeInit ? pageSizeInit.toString() : "0"),
      filter: (filter || ""),
      orderBy: orderBy || (orderByInit ? orderByInit : undefined),
      search: search || "",
    });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAllQuery = useQuery({
    queryKey: [APIname, queryValue.search, queryValue.orderBy, queryValue.page, queryValue.pageSize, queryValue.filter, preFilter, ...queryKey ],
    queryFn: () =>
      API.getAll({
        page: parseInt(queryValue.page),
        pageSize: parseInt(queryValue.pageSize),
        filterQuery: (preFilter? prefilterString :"")+(preFilter && queryValue.filter ? "|": "")+ (queryValue.filter? queryValue.filter : ""),
        orderBy: queryValue.orderBy as any,
        search: queryValue.search,
        filter: directFilter
      }),
    enabled: !disableGet && !!queryValue.page && queryValue.pageSize !== "0",
  });

  const setFilterObject = (filterObject?: FilterObject<V>) => {
    setFilter(undefined, filterObject);
  }


  const removeFromFilter = (keys: [keyof T]) => {
 
    let filternew = {...currentFilterObject};
    keys.forEach((key) => {
        filternew = {...filternew, [key]: undefined}
    });
    setFilter(undefined, filternew);
  }

  const setFilter = (filter?: Filter<V>, filterObject?: FilterObject<V>) => {

    const filterObjectConverted = convertFilterObjectToRawFilter(filterObject) || {};
    const filterFomated = {...filter, ...filterObjectConverted};
    const filterString = convertFilterToWhereQuery(filterFomated);


    if (filterString === "" || filterString === undefined) {
      removeQueryValueURL([queryKeyName.filter]);
      return;
    }
    setQueryValueURL({ [queryKeyName.filter]: filterString });
  };
  const setPage = (page: number) => {
    setQueryValueURL({ [queryKeyName.page]: page.toString() });
  };
  const setPageSize = (pageSize: number) => {
    setQueryValueURL({ [queryKeyName.pageSize]: pageSize.toString(), [queryKeyName.page]: "1" });
  };

  const setOrderBy = (orderBy: string) => {
    setQueryValueURL({ [queryKeyName.orderBy]: orderBy, [queryKeyName.page]: "1" });
  };

  const createNew = useMutation({
    mutationFn: ({
      newData,
      callback,
    }: {
      newData: X;
      callback?: CallbackFunction<T, AxiosError>;
    }) =>
      API.create(newData)
        .then((res) => {
          const result = res as T;
          callback && callback({ result });
          return Promise.resolve(res);
        })
        .catch((error) => {
          callback && callback({ error });
          return Promise.reject(error);
        }),

    onSuccess: () => {
      getAllQuery.refetch();
      queryClient.invalidateQueries(APIname);
      onSucessRefetchRessources &&
      onSucessRefetchRessources.forEach((ressource) => {
        queryClient.invalidateQueries(ressource);
      });
    },
  });

  const deleteByIdQuery = useMutation({
    mutationFn: ({
      id,
      callback,
    }: {
      id: string;
      callback?: CallbackFunction<any, AxiosError>;
    }) =>
      API.delete(id)
        .then((res) => {
          callback && callback({ result: res });
          return Promise.resolve(res);
        })
        .catch((error) => {
          callback && callback({ error });
          return Promise.reject(error);
        }),
    onSuccess: () => {
      getAllQuery.refetch();
      queryClient.invalidateQueries(APIname);
      onSucessRefetchRessources &&
      onSucessRefetchRessources.forEach((ressource) => {
        queryClient.invalidateQueries(ressource);
      });
    },
  });

  const haveNextPage = () => {
    return (
      getAllQuery.data?.pagination &&
      getAllQuery.data.pagination.total_pages >
        getAllQuery.data.pagination.current_page
    );
  };

  const havePrevPage = () => {
    return (
      getAllQuery.data?.pagination &&
      getAllQuery.data.pagination.current_page > 1
    );
  };

  const nextPage = () => {
    if (!haveNextPage) {
      return;
    }
    getAllQuery.data?.pagination &&
      setPage(getAllQuery.data.pagination.current_page + 1);
  };

  const prevPage = () => {
    if (!havePrevPage) {
      return;
    }
    getAllQuery.data?.pagination &&
      setPage(getAllQuery.data.pagination.current_page - 1);
  };

  const setSearch = (search: string | null | undefined) => {
    if (!search) {
      removeQueryValueURL([queryKeyName.search]);
      return;
    }
    setQueryValueURL({ [queryKeyName.search]: search, [queryKeyName.page]: "1"  });
  }



  return {
    queryKeyName,
    createNew,
    deleteByIdQuery: {
      ...deleteByIdQuery,
      error: deleteByIdQuery.error as AxiosError,
    },
    getAllQuery: {
      ...getAllQuery,
      error: getAllQuery.error as AxiosError,
      setFilter,
      setPage,
      setPageSize,
      setOrderBy,
      haveNextPage,
      havePrevPage,
      nextPage,
      prevPage,
      setSearch,
      sreachValue: queryValue.search,
      currentFilter,
      currentFilterObject,
      setFilterObject,
      removeFromFilter

    },
  };
}

// T : type of data
// X : type of data for create put
// V : type of Error
export function useSingleGenericAPI<T, X>({
  baseURL,
  id = "",
  APIname,
  APIObject,
  onSucessRefetchRessources,
  queryKey = [],
}: IUseSingleGenericAPI<T, X>) {
  if (!APIObject && !baseURL) {
    throw new Error("baseURL or APIObject is required");
  }
  const API = APIObject || new APIGenerator<T, X, any>(baseURL || "");

  const queryClient = useQueryClient();
  const singleQuery = useQuery({
    queryKey: [APIname, id, ...queryKey],
    queryFn: () => API.getSingle(id),
    enabled: !!id && id !== "",
  });

  const deleteQuery = useMutation({
    mutationFn: ({
      callback,
    }: {
      callback?: CallbackFunction<any, AxiosError>;
    }) =>
      API.delete(id)
        .then((res) => {
          callback && callback({ result: res });
          return Promise.resolve(res);
        })
        .catch((error) => {
          callback && callback({ error });
          return Promise.reject(error);
        }),
    onSuccess: () => {
      queryClient.invalidateQueries(APIname);
      onSucessRefetchRessources &&
        onSucessRefetchRessources.forEach((ressource) => {
          queryClient.invalidateQueries(ressource);
        });
    },
  });

  const updateQuery = useMutation({
    mutationFn: ({
      data,
      callback,
    }: {
      data: X;
      callback?: CallbackFunction<T, AxiosError>;
    }) =>
      API.update(id, data)
        .then((res) => {
          callback && callback({ result: res });
          return Promise.resolve(res);
        })
        .catch((error) => {
          callback && callback({ error });
          return Promise.reject(error);
        }),
    onSuccess: () => {
      singleQuery.refetch();
      queryClient.invalidateQueries(APIname);
      onSucessRefetchRessources &&
      onSucessRefetchRessources.forEach((ressource) => {
        queryClient.invalidateQueries(ressource);
      });
    },
  });

  const patchQuery = useMutation({
    mutationFn: ({
      data,
      callback,
    }: {
      data: X;
      callback?: CallbackFunction<T, AxiosError>;
    }) =>
      API.update(id, {...singleQuery.data,...data})
        .then((res) => {
          callback && callback({ result: res });
          return Promise.resolve(res);
        })
        .catch((error) => {
          callback && callback({ error });
          return Promise.reject(error);
        }),
    onSuccess: () => {
      singleQuery.refetch();
      queryClient.invalidateQueries(APIname);
      onSucessRefetchRessources &&
      onSucessRefetchRessources.forEach((ressource) => {
        queryClient.invalidateQueries(ressource);
      });
    },
  });

  return {
    singleQuery: {
      ...singleQuery,
      error: singleQuery.error as AxiosError,
    },
    deleteQuery: {
      ...deleteQuery,
      error: deleteQuery.error as AxiosError,
    },
    updateQuery: {
      ...updateQuery,
      error: updateQuery.error as AxiosError,
    },
    patchQuery: {
      ...patchQuery,
      error: patchQuery.error as AxiosError,
    },
    id
  };
}

/**
 *
 * @returns
 */
function useQueryURL() {
  const location: string = useLocation().search;
  const navigate = useNavigate();
  const getQueryValue = (queryKey: string) => {
    const urlParams = new URLSearchParams(location);
    return urlParams.get(queryKey);
  };
  const setQueryValue = (
    list: { [key: string]: string },
    navigateOptions?: NavigateOptions
  ) => {
    const urlParams = new URLSearchParams(location);
    for (const [key, value] of Object.entries(list)) {
      urlParams.set(key, value);
    }
    const path = location.split("?")[0] + "?" + urlParams.toString();
    navigate(path, navigateOptions || { replace: true });
  };
  const removeQueryValue = (
    queryKey: string[],
    navigateOptions?: NavigateOptions
  ) => {
    const urlParams = new URLSearchParams(location);
    queryKey.forEach((key) => urlParams.delete(key));
    const path = location.split("?")[0] + "?" + urlParams.toString();
    navigate(path, navigateOptions || { replace: true });
  };

  return {
    getQueryValue,
    setQueryValue,
    removeQueryValue,
    currentLocation: location,
  };
}
